<template>
    <div>
        <b-container class="p-0" fluid>
            <b-row>
                <b-col>
                    <h2>{{ $t("contacts.lists.wizard.import-form-title") }}</h2>

                    <b-form-group
                        v-slot="{ ariaDescribedby }"
                        :label="$t('contacts.lists.wizard.import-form-label-existing')"
                        content-cols-lg="7"
                        content-cols-sm
                        label-cols-lg="3"
                        label-cols-sm="4"
                        label-for="update-ignore"
                    >
                        <b-form-radio
                            v-model="updateOrIgnore"
                            :aria-describedby="ariaDescribedby"
                            name="update-ignore"
                            value="update"
                            @change="setUpdateExistingContacts"
                            ><strong>{{ $t("contacts.lists.wizard.import-form-label-existing-update") }}</strong>
                            {{ $t("contacts.lists.wizard.import-form-label-existing-update-info") }}
                        </b-form-radio>
                        <b-form-radio
                            v-model="updateOrIgnore"
                            :aria-describedby="ariaDescribedby"
                            name="update-ignore"
                            value="ignore"
                            @change="setUpdateExistingContacts"
                            ><strong>{{ $t("contacts.lists.wizard.import-form-label-existing-ignore") }}</strong>
                            {{ $t("contacts.lists.wizard.import-form-label-existing-ignore-info") }}
                        </b-form-radio>
                    </b-form-group>

                    <b-form-group
                        v-slot="{ ariaDescribedby }"
                        :label="$t('contacts.lists.wizard.import-form-label-delimiter')"
                        content-cols-lg="7"
                        content-cols-sm
                        label-cols-lg="3"
                        label-cols-sm="4"
                        label-for="delimeter"
                    >
                        <b-form-radio
                            v-model="delimiter"
                            :aria-describedby="ariaDescribedby"
                            name="delimeter"
                            value=","
                            @change="setUpdateExistingContacts"
                            ><strong>{{ $t("contacts.lists.wizard.import-form-label-delimiter-comma") }}</strong>
                            {{ $t("contacts.lists.wizard.import-form-label-delimiter-comma-info") }}
                        </b-form-radio>
                        <b-form-radio
                            v-model="delimiter"
                            :aria-describedby="ariaDescribedby"
                            name="delimeter"
                            value=";"
                            @change="setUpdateExistingContacts"
                            ><strong>{{ $t("contacts.lists.wizard.import-form-label-delimiter-semicolon") }}</strong>
                            {{ $t("contacts.lists.wizard.import-form-label-delimiter-semicolon-info") }}
                        </b-form-radio>
                    </b-form-group>

                    <b-form-group
                        :label="$t('contacts.lists.wizard.import-form-label-file')"
                        content-cols-lg="7"
                        content-cols-sm
                        label-cols-lg="3"
                        label-cols-sm="4"
                    >
                        <b-form-file
                            id="input-csv"
                            :disabled="isLoading"
                            accept="text/csv"
                            class="w-75"
                            type="file"
                            @change="parseCSV"
                            @click="clearFileInput"
                        ></b-form-file>
                        <b-button
                            id="modal-info"
                            class="ml-1 p-0 info-square"
                            size="sm"
                            variant="primary"
                            @click="$emit('open-modal')"
                            >i
                        </b-button>
                    </b-form-group>
                </b-col>
            </b-row>
        </b-container>

        <hr />

        <template v-if="invalidCsvError">
            <b-alert class="p-50" show variant="danger">
                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="error" size="lg" />
                {{ invalidCsvError }}
            </b-alert>
        </template>
        <template v-if="errorsCount > 0">
            <h3>{{ $tc("contacts.lists.wizard.import-form-errors-title", errorsCount) }}</h3>
            <template v-for="(parseErrorRows, rowNumber) in errorWarningsObject.errors">
                <b-alert class="p-50 d-flex" show variant="danger">
                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-50" size="lg" />
                    <div>
                        <strong>{{ $t("row") }} {{ parseInt(rowNumber) }}</strong
                        ><br />
                        <template v-for="parseError in parseErrorRows">
                            {{ parseError["column"] ? `${$t("column")} '${parseError["column"]}':` : "" }}
                            {{ $t(parseError.warning || parseError.error) }}<br />
                        </template>
                    </div>
                </b-alert>
            </template>
        </template>
        <template v-if="warningsCount > 0">
            <h3>{{ $tc("contacts.lists.wizard.import-form-warnings-title", warningsCount) }}</h3>
            <template v-for="(parseErrorRows, rowNumber) in errorWarningsObject.warnings">
                <b-alert class="p-50 d-flex" show variant="warning">
                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-50" size="lg" />
                    <div>
                        <strong>{{ $t("row") }} {{ parseInt(rowNumber) }}</strong
                        ><br />
                        <template v-for="parseError in parseErrorRows">
                            {{ parseError["column"] ? `${$t("column")} '${parseError["column"]}':` : "" }}
                            {{ $t(parseError.warning) }}<br />
                        </template>
                    </div>
                </b-alert>
            </template>
        </template>

        <div v-if="rowData.length > 0 && !hasErrors" class="import-results">
            <div class="d-flex justify-content-end">
                <b-button class="mb-1 mr-1" variant="primary" @click="selectAllRows"
                    >{{ $t("contacts.lists.wizard.select-all") }}
                </b-button>
                <b-button class="mb-1" variant="light" @click="clearSelected"
                    >{{ $t("contacts.lists.wizard.select-none") }}
                </b-button>
            </div>

            <b-table
                ref="csvContactsTable"
                :fields="fields"
                :items="rowData"
                :no-border-collapse="false"
                :no-sort-reset="true"
                :sticky-header="true"
                responsive
                select-mode="multi"
                selectable
                @row-selected="onRowSelected"
            >
                <template #head(id)="scope">
                    <div class="text-nowrap">Row ID</div>
                </template>
                <template #head()="scope">
                    <div class="text-nowrap">{{ scope.label }}</div>
                </template>
            </b-table>
        </div>
        <b-alert v-else-if="rowData.length === 0" class="p-50" show variant="info">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" size="lg" />&nbsp;{{
                $t("contacts.lists.wizard.import-contacts-none")
            }}
        </b-alert>
    </div>
</template>

<script>
import { nextTick } from "@vue/composition-api";
import { BContainer, BRow, BCol, BFormGroup, BFormRadio, BFormFile, BTable, BButton, BAlert } from "bootstrap-vue";
import * as EmailValidator from "email-validator";
//import { RecycleScroller } from "vue-virtual-scroller";
//import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import * as lodash from "lodash";
//import InformationTooltip from "@/components/messaging/ui-kit/information-tooltip";

import csvParse from "@/libs/csvParse";
import { useToast } from "vue-toastification/composition";
import toastificationContent from "@/layouts/components/toastification/toastificationContent";
import i18n from "@/libs/i18n";
import { parsePhoneNumberFromString } from "libphonenumber-js";

export default {
    components: {
        BContainer,
        BRow,
        BCol,
        BFormGroup,
        BFormRadio,
        BFormFile,
        BTable,
        BButton,
        BAlert,
        //RecycleScroller,
        //InformationTooltip,
    },
    data() {
        return {
            isLoading: false,
            maxNumberOfRows: 23000,
            updateOrIgnore: "update",
            delimiter: ",",
            globalParsers: [
                {
                    message: "contacts.lists.wizard.import-form-error-email-phone-required",
                    validator: (contact) => contact.phone || contact.email,
                },
            ],
            defaultColumns: {
                name: { maxLength: 255, required: true, expression: "^[^<>]+$" },
                email: {
                    maxLength: 255,
                    required: false,
                    validator: (email) => EmailValidator.validate(email),
                },
                phone: {
                    maxLength: 255,
                    required: false,
                    validator: (number) => parsePhoneNumberFromString(number)?.isValid(),
                    parser: (number) => parsePhoneNumberFromString(number)?.formatInternational(),
                },
                street: { maxLength: 255, required: false, expression: "^[^<>]+$" },
                houseNumber: { maxLength: 50, required: false, expression: "^[^<>]+$" },
                zipCode: { maxLength: 50, required: false, expression: "^[a-zA-Z0-9\\s]+$" },
                city: { maxLength: 255, required: false, expression: "^[^<>]+$" },
                province: { maxLength: 255, required: false, expression: "^[^<>]+$" },
                countryCode: { maxLength: 2, required: false, expression: "^[A-Z]{2}$" },
                reference: { maxLength: 255, required: false, expression: "^[^<>]+$" },
                company: { maxLength: 255, required: false, expression: "^[^<>]+$" },
                jobTitle: { maxLength: 255, required: false, expression: "^[^<>]+$" },
            },
            rowData: [],
            fields: [],
            selected: [],
            fileName: "",
            parseErrors: {},
            hasErrors: false,
            invalidCsvError: "",
        };
    },
    computed: {
        // 2022-12-8 Marcello: @TODO move error checking to composition api and refactor for seperate errors and warnings
        errorWarningsObject() {
            const errors = {};
            const warnings = {};

            for (let rowNumber in this.parseErrors) {
                if (this.rowContainsErrors(this.parseErrors[rowNumber])) {
                    errors[rowNumber] = this.parseErrors[rowNumber];
                } else {
                    warnings[rowNumber] = this.parseErrors[rowNumber];
                }
            }

            return { errors, warnings };
        },
        errorsCount() {
            return Object.keys(this.errorWarningsObject.errors).length;
        },
        warningsCount() {
            return Object.keys(this.errorWarningsObject.warnings).length;
        },
    },
    watch: {
        hasErrors(value) {
            this.$emit("error-update", value);
        },
    },
    methods: {
        // Chrome doesn't detect onChange when file is the same
        // Reset the file onClick so we force an update
        clearFileInput(event) {
            event.target.value = null;
        },
        parseCSV(event) {
            this.resetForm();

            const files = event.target.files;

            if (!files.length) {
                return;
            }

            this.isLoading = true;

            // Set filename
            this.fileName = files[0].name;

            // Max size in bytes (2MB max for now)
            //if (files[0].size > 2048000) {
            if (files[0].size > 5120000) {
                this.toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("contacts.lists.wizard.import-form-errors-title", 1),
                        //text: i18n.t("contacts.lists.wizard.error.max-file-size", { fileSize: "2MB" }),
                        text: i18n.t("contacts.lists.wizard.error.max-file-size", { fileSize: "5MB" }),
                        icon: "times",
                        variant: "danger",
                    },
                });

                return;
            }

            const reader = new FileReader(); // no arguments
            reader.readAsText(files[0]);

            reader.onloadend = () => {
                // Max number of rows that can be imported is limited to 1000
                // Because columns (sets the first row as array keys) is enabled, we need to go one line further
                csvParse(reader.result, {
                    // https://csv.js.org/parse/options/
                    bom: true,
                    columns: true,
                    delimiter: this.delimiter,
                    trim: true,

                    // on_record we set an internal id to work with (___rowId___) in our logic
                    on_record: (record, { lines }) => ({ ___rowId___: lines, ...record }),

                    // Validate data
                    cast: (value, context) => {
                        if (context.header === true) {
                            this.fields.push(value);
                        }

                        // Only check NON header values
                        if (context.header === false) {
                            // EMPTY
                            // ANY Empty values throw warnings/errors, because they can cause deletion of data
                            if (value.length === 0) {
                                const message =
                                    context.column in this.defaultColumns &&
                                    this.defaultColumns[context.column].required === true
                                        ? { error: "contacts.lists.wizard.import-form-error-empty-values" }
                                        : { warning: "contacts.lists.wizard.import-form-warning-empty-values" };
                                this.addParseError(context.records + 2, { column: context.column, value, ...message });

                                return value;
                            }

                            // DEFAULT COLUMNS
                            // Check it's values, set in this.defaultColumns
                            // Check case insensitive too, because "zipCode" will be metadata if set to wrong key like "zipcode"
                            // keyInObjectCaseInsensitive returns the right key we need to check for in the object
                            const contextColumn = this.keyInObjectCaseInsensitive(context.column, this.defaultColumns);

                            if (contextColumn.length > 0) {
                                // Check if the value for max string length
                                if (value.length > this.defaultColumns[contextColumn].maxLength) {
                                    this.addParseError(context.records + 2, {
                                        column: context.column,
                                        value: value,
                                        error: "contacts.lists.wizard.import-form-error-exceeds-length",
                                    });
                                }

                                // Check the text with a regex (if available)
                                if ("expression" in this.defaultColumns[contextColumn]) {
                                    const regex = new RegExp(this.defaultColumns[contextColumn].expression);

                                    if (regex.test(value) === false) {
                                        this.addParseError(context.records + 2, {
                                            column: context.column,
                                            value: value,
                                            error: "contacts.lists.wizard.import-form-error-invalid-value",
                                        });
                                    }
                                }

                                // Check the text with a custom function (if available)
                                if ("validator" in this.defaultColumns[contextColumn]) {
                                    if (!this.defaultColumns[contextColumn].validator(value)) {
                                        this.addParseError(context.records + 2, {
                                            column: context.column,
                                            value: value,
                                            error: "contacts.lists.wizard.import-form-error-invalid-value",
                                        });
                                    }
                                }

                                // Return a parsed value (if available)
                                if ("parser" in this.defaultColumns[contextColumn]) {
                                    return this.defaultColumns[contextColumn].parser(value);
                                }

                                return value;
                            }

                            // CUSTOM COLUMNS
                            // Can be max 50 chars and can contain anything
                            if (value.length > 50) {
                                this.addParseError(context.records + 2, {
                                    column: context.column,
                                    value: value,
                                    error: "contacts.lists.wizard.import-form-error-exceeds-length",
                                });

                                return value;
                            }
                        }

                        return value;
                    },
                })
                    .then((output) => {
                        this.isLoading = false;

                        const phoneNumberMap = {};
                        const emailMap = {};

                        // Check for errors in the global parsers
                        output.forEach((contact) => {
                            this.globalParsers.forEach((parser) => {
                                if (parser.validator(contact)) {
                                    return;
                                }

                                this.addParseError(contact.___rowId___, {
                                    error: parser.message,
                                });
                            });

                            // Populate maps
                            if (contact.email) {
                                const email = contact.email.toLowerCase();
                                if (emailMap[email]) {
                                    emailMap[email].push(contact.___rowId___);
                                } else {
                                    emailMap[email] = [contact.___rowId___];
                                }
                            }
                            if (contact.phone) {
                                try {
                                    const phone = parsePhoneNumberFromString(contact.phone).formatInternational();
                                    if (phoneNumberMap[phone]) {
                                        phoneNumberMap[phone].push(contact.___rowId___);
                                    } else {
                                        phoneNumberMap[phone] = [contact.___rowId___];
                                    }
                                } catch {} // Parsing the phonenumber went wrong, there should already be an error for this
                            }
                        });

                        // Add any duplicates to the parseErrors list
                        Object.values(emailMap).forEach((rows) => {
                            if (rows.length > 1) {
                                rows.forEach((row) =>
                                    this.addParseError(row, {
                                        error: "contacts.lists.wizard.import-form-error-duplicate-email",
                                    })
                                );
                            }
                        });
                        Object.values(phoneNumberMap).forEach((rows) => {
                            if (rows.length > 1) {
                                rows.forEach((row) =>
                                    this.addParseError(row, {
                                        error: "contacts.lists.wizard.import-form-error-duplicate-phone",
                                    })
                                );
                            }
                        });

                        // Force update parseErrors so computed properties know to change
                        this.parseErrors = { ...this.parseErrors };

                        // Validate if we have selected the right delimiter
                        // We only got this if we selected the non-default from csvParse :(
                        // To improve the UX this error is highlighted too
                        // Explained:
                        // To check this we take a look at the object key where the column names are stored
                        // We check this in the first entry of 'output'
                        // If a wrong delimiter is selected we get two keys:
                        // '___rowId___' we ignore this one
                        // 'string;string;string' this is the one we want to check
                        // If we use Object.keys() and we always can pick the second item and
                        // check there if we have the "other" delimiter available
                        // If that is the case this could be a sign that the wrong delimiter is selected
                        // otherwise there is more wrong and we throw a more generic error
                        // Note: If someone uses a csv with combined delimiters,
                        // it will also fail but with a more generic error because this is just bad practice
                        if (Object.keys(output[0]).length === 2) {
                            // Select the other not selected delimiter
                            let otherDelimiter = this.delimiter === "," ? ";" : ",";

                            if (Object.keys(output[0])[1].includes(otherDelimiter)) {
                                this.toast({
                                    component: toastificationContent,
                                    props: {
                                        title: i18n.t("contacts.lists.wizard.import-form-errors-title", 1),
                                        text: i18n.t("contacts.lists.wizard.error.wrong-delimiter"),
                                        icon: "times",
                                        variant: "danger",
                                    },
                                });

                                this.resetForm();
                                return;
                            }
                        }

                        // Validate we don't have more rows than we can import
                        if (output.length > this.maxNumberOfRows) {
                            this.toast({
                                component: toastificationContent,
                                props: {
                                    title: i18n.t("contacts.lists.wizard.import-form-errors-title", 1),
                                    text: i18n.t("contacts.lists.wizard.error.max-num-rows", {
                                        maxRows: this.maxNumberOfRows,
                                    }),
                                    icon: "times",
                                    variant: "danger",
                                },
                            });

                            this.resetForm();
                            return;
                        }

                        // Validate the first row on it's columns
                        // No more than 20 columns
                        const columnsToImport = lodash.keysIn(output[0]);

                        if (columnsToImport.length > 30) {
                            this.toast({
                                component: toastificationContent,
                                props: {
                                    title: i18n.t("contacts.lists.wizard.import-form-errors-title", 1),
                                    text: i18n.t("contacts.lists.wizard.error.max-num-columns", { maxColumns: 30 }),
                                    icon: "times",
                                    variant: "danger",
                                },
                            });

                            this.resetForm();
                            return;
                        }

                        // Validate if the mandatory column names are available
                        // For now we need at least "name" and "phone" for whatsapp
                        const columnNamesMandatory = ["name", "phone"];
                        const isValid = columnsToImport.filter((column) =>
                            columnNamesMandatory.includes(column.toLowerCase())
                        );

                        if (isValid.length !== columnNamesMandatory.length) {
                            this.toast({
                                component: toastificationContent,
                                props: {
                                    title: i18n.t("contacts.lists.wizard.import-form-errors-title", 1),
                                    text: i18n.t("contacts.lists.wizard.error.mandatory-columns"),
                                    icon: "times",
                                    variant: "danger",
                                },
                            });

                            this.resetForm();
                            return;
                        }

                        // Output is validated, add it as row data and select all
                        // We also set column names to their right formatting
                        // "zipcode" OR "ZiPCodE" should be "zipCode", (note: we checked/validated the values earlier in the "cast" function)
                        this.rowData = this.fixKeyFormatting(output);
                        // this.selected = lodash.map(this.rowData, (row) => {
                        //     return row.___rowId___;
                        // });

                        // this.updateSelection();

                        // Wait for the DOM to update and show the table with rowData
                        if (!this.hasErrors) {
                            nextTick().then(() => {
                                this.selectAllRows();
                            });
                        }
                    })
                    .catch((error) => {
                        // Catch csvParse errors
                        // https://csv.js.org/parse/errors/
                        this.resetForm();

                        // For now we only early return if we have a wrong delimiter error
                        if (error.code === "CSV_INVALID_CLOSING_QUOTE") {
                            this.toast({
                                component: toastificationContent,
                                props: {
                                    title: i18n.t("contacts.lists.wizard.import-form-errors-title", 1),
                                    text: i18n.t("contacts.lists.wizard.error.wrong-delimiter"),
                                    icon: "times",
                                    variant: "danger",
                                },
                            });

                            return;
                        }

                        this.invalidCsvError = error;

                        // Probably a wrong file, can't parse as csv
                        this.toast({
                            component: toastificationContent,
                            props: {
                                title: i18n.t("contacts.lists.wizard.import-form-errors-title", 1),
                                text: i18n.t("contacts.lists.wizard.error.csv-file-only"),
                                icon: "times",
                                variant: "danger",
                            },
                        });
                    });
            };

            reader.onerror = () => {
                this.isLoading = false;

                this.toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("contacts.lists.wizard.import-form-errors-title", 1),
                        text: i18n.t("contacts.lists.wizard.error.unknown"),
                        icon: "times",
                        variant: "danger",
                    },
                });
            };
        },
        onRowSelected(items) {
            this.selected = items;
            this.updateSelection();
        },
        selectAllRows() {
            this.$refs.csvContactsTable.selectAllRows();
            this.updateSelection();
        },
        clearSelected() {
            this.$refs.csvContactsTable.clearSelected();
            this.updateSelection();
        },
        updateSelection() {
            // Update the selection in the parent with complete contact objects
            this.$emit(
                "update-selection",
                this.selected
                //this.rowData.filter((contact) => this.selected.includes(contact.___rowId___))
            );
        },
        setUpdateExistingContacts() {
            const updateExisting = this.updateOrIgnore !== "ignore";
            this.$emit("update-existing-contacts", updateExisting);
        },
        selectMany() {
            // If all are selected, just clear the array
            if (this.selected.length === this.rowData.length) {
                this.selected = [];
            } else {
                // In any other case, select all
                this.selected = this.rowData;
            }

            // Update the selection in the parent
            this.updateSelection();
        },
        resetForm() {
            //this.updateOrIgnore = "update";
            //this.delimiter = ",";
            this.rowData = [];
            this.selected = [];
            this.fileName = "";

            this.parseErrors = {};
            this.hasErrors = false;
            this.invalidCsvError = "";

            this.isLoading = false;
        },
        addField(fieldName) {
            this.fields.push(fieldName);
        },
        addParseError(lineNumber, error) {
            // Push the line in the error object
            if (lineNumber in this.parseErrors === false) {
                this.parseErrors[lineNumber] = [];
            }

            this.parseErrors[lineNumber].push(error);

            // Could receive a warning, which should only be mentioned and not stopping from import,
            // so set hasErrors to true if the line is an error
            if ("error" in error) {
                this.hasErrors = true;
            }
        },
        rowContainsErrors(parseErrorRows) {
            if (lodash.findKey(parseErrorRows, "error")) {
                return true;
            }

            return false;
        },
        removeRow(objectIndex) {
            // Remove the index (wich is row number) from the parse error object
            this.parseErrors = lodash.filter(this.parseErrors, function (parseErrorRow, index) {
                if (index !== objectIndex) {
                    return parseErrorRow;
                }
            });
        },
        keyInObjectCaseInsensitive(searchKey, searchObject) {
            return (
                Object.keys(searchObject).find(
                    (key) => key.toString().toLowerCase() === searchKey.toString().toLowerCase()
                ) || ""
            );
        },
        fixKeyFormatting(importedData) {
            return importedData.map((row) => {
                const fixedRow = {};

                lodash.forEach(row, (columnValue, columnName) => {
                    fixedRow[this.keyInObjectCaseInsensitive(columnName, this.defaultColumns) || columnName] =
                        columnValue;
                });
                return { ...fixedRow, labels: [] };
            });
        },
    },
    setup() {
        const toast = useToast();

        return { toast };
    },
};
</script>
